import React from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import type { InventoryItemResponse } from '../../../api/types';
import type { PrimaryPhotoRecord } from '../segmentSlice';
import { PrimaryPhotoCard } from './PrimaryPhotoCard';

/**
 * Hero grid for the post-confirmation Done view. The user just
 * tapped Save → server kicked off crop+upscale jobs for every
 * accepted item; we open SSE streams in parallel and update the
 * card grid as URLs land.
 *
 * 2-column responsive grid on mobile, 3 columns above 600 px.
 * Card progression: skeleton → ready (upscaled) → fallback (room
 * photo if upscale failed) → timeout/error (room photo + warning).
 *
 * Top of the view: a sticky completion header with item count +
 * "polishing" progress (N of M ready). CTA at the bottom routes
 * back to the inventory dashboard or starts a fresh scan.
 */

interface DoneViewProps {
  acceptedItems: InventoryItemResponse[];
  primaryPhotos: Record<number, PrimaryPhotoRecord>;
  fallbackRoomPhotoUrl?: string | null;
  /** "Done" CTA — caller decides where to go (Browse vs new scan). */
  onFinish: () => void;
  /** Optional secondary CTA — fresh scan from this screen. */
  onScanAgain?: () => void;
}

export const DoneView: React.FC<DoneViewProps> = ({
  acceptedItems,
  primaryPhotos,
  fallbackRoomPhotoUrl,
  onFinish,
  onScanAgain,
}) => {
  const total = acceptedItems.length;
  const ready = acceptedItems.filter((it) => {
    const id = it.id;
    if (typeof id !== 'number') return false;
    const p = primaryPhotos[id];
    return p?.state === 'ready';
  }).length;
  const polished = ready === total && total > 0;

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollBody}
    >
      <View style={styles.header}>
        <View style={styles.headerIcon}>
          <MaterialIcons
            name={polished ? 'check-circle' : 'auto-awesome'}
            size={22}
            color={fpColors.primary}
          />
        </View>
        <View style={styles.headerText}>
          <Text style={styles.title}>
            {polished ? 'Saved to your inventory' : 'Polishing your items\u2026'}
          </Text>
          <Text style={styles.subtitle}>
            {polished
              ? `${total} item${total === 1 ? '' : 's'} ready to browse.`
              : `${ready} of ${total} polished\u2026 we'll keep working in the background.`}
          </Text>
        </View>
      </View>

      <View style={styles.grid}>
        {acceptedItems.map((it) => {
          const id = it.id ?? -1;
          return (
            <View key={`done-${id}`} style={styles.gridCell}>
              <PrimaryPhotoCard
                item={it}
                photo={id > 0 ? primaryPhotos[id] : undefined}
                fallbackRoomPhotoUrl={fallbackRoomPhotoUrl}
              />
            </View>
          );
        })}
      </View>

      <View style={styles.ctaRow}>
        {onScanAgain ? (
          <Pressable
            onPress={onScanAgain}
            style={({ pressed }) => [
              styles.btnSecondary,
              pressed && { opacity: 0.85 },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Scan another room"
          >
            <MaterialIcons
              name="add-a-photo"
              size={14}
              color={fpColors.onSurface}
            />
            <Text style={styles.btnSecondaryText}>Scan another</Text>
          </Pressable>
        ) : null}
        <Pressable
          onPress={onFinish}
          style={({ pressed }) => [
            styles.btnPrimary,
            pressed && { opacity: 0.88 },
          ]}
          accessibilityRole="button"
          accessibilityLabel="Done"
        >
          <Text style={styles.btnPrimaryText}>Done</Text>
          <MaterialIcons
            name="arrow-forward"
            size={14}
            color={fpColors.onPrimary}
          />
        </Pressable>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scroll: { flex: 1, backgroundColor: fpColors.background },
  scrollBody: { paddingHorizontal: 16, paddingVertical: 18, gap: 16 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: `${fpColors.primary}14`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerText: { flex: 1, gap: 2 },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  subtitle: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurfaceVariant,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  gridCell: {
    // 2-column at default; calc gives a 50% minus half-the-gap width.
    width: 'calc(50% - 5px)' as any,
  },
  ctaRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    marginTop: 4,
  },
  btnSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 11,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  btnSecondaryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  btnPrimary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 13,
    borderRadius: 999,
    backgroundColor: fpColors.primary,
  },
  btnPrimaryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimary,
  },
});
